import {Subscription} from '../subscription';

/**
 * gateway_id prefix marking a no-card trial row. Mirrors
 * Common\Billing\Subscription::TRIAL_GATEWAY_ID_PREFIX.
 */
export const TRIAL_GATEWAY_ID_PREFIX = 'trial:';

type SubscriptionLike = Pick<Subscription, 'gateway_id'> &
  Partial<Pick<Subscription, 'on_trial' | 'valid'>>;

/**
 * Whether this is a free-trial row at all - running, ended or converted.
 * Mirrors the backend's isTrialRow().
 */
export function isTrialSubscriptionRow(
  subscription:
    | (Pick<Subscription, 'gateway_id'> &
        Partial<Pick<Subscription, 'trial_ends_at'>>)
    | null
    | undefined,
): boolean {
  return (
    !!subscription &&
    typeof subscription.gateway_id === 'string' &&
    subscription.gateway_id.startsWith(TRIAL_GATEWAY_ID_PREFIX) &&
    !!subscription.trial_ends_at
  );
}

/**
 * Whether this is a free-trial row that is still running.
 *
 * Mirrors the backend's isTrialRow() + valid(): the gateway_id marker AND a
 * running trial date. `on_trial` alone is not enough - anything that ever sets
 * trial_ends_at (e.g. a card trial later on) would otherwise be mistaken for a
 * code trial.
 */
export function isRunningTrialSubscription(
  subscription: SubscriptionLike | null | undefined,
): boolean {
  return (
    !!subscription &&
    !!subscription.on_trial &&
    typeof subscription.gateway_id === 'string' &&
    subscription.gateway_id.startsWith(TRIAL_GATEWAY_ID_PREFIX)
  );
}

/**
 * THE definition of "this user is on a free trial", used everywhere.
 *
 * True only when the user holds at least one valid subscription and EVERY
 * valid subscription is a running trial. So a paying customer is never "on
 * trial" (even with a stale trial row left open), and neither is a customer
 * who cancelled but still has paid days left - that one has the same
 * valid + cancelled + on_grace_period shape as a trial, and treating them as
 * on trial would let them subscribe, and be charged, a second time.
 */
export function isOnTrialFromSubscriptions(
  subscriptions: SubscriptionLike[] | null | undefined,
): boolean {
  const valid = (subscriptions ?? []).filter(sub => sub.valid);
  return valid.length > 0 && valid.every(isRunningTrialSubscription);
}
